/**
 * Product gallery: fit media to the viewport height.
 *
 * The theme sizes gallery media with the padding-top aspect-ratio hack
 * (snippets/lazy-image.liquid), which is width-driven — so a tall portrait
 * grows past the fold. There's no pure-CSS fix, because the per-image ratio
 * only exists as a `data-ratio` attribute and CSS can't read it.
 *
 * So: read data-ratio, and cap the figure's WIDTH such that the derived
 * height lands inside the viewport.
 *
 *   height = width / ratio   →   width = height * ratio
 *
 * We constrain `.lazy-image` (the figure) rather than `.product-gallery__item`
 * (the slide) on purpose:
 *   - the slide keeps its grid width, so the slider's translate maths is
 *     untouched;
 *   - component-product-slider.js `_setHeight()` measures
 *     `.lazy-image`.offsetHeight, so the viewport height follows for free;
 *   - the padding-top hack still reserves space before load, so no CLS.
 */
(function () {
  'use strict';

  var cfg = window.productGalleryFitConfig || {};
  var GUTTER = cfg.gutter || 140; // px reserved for header + breathing room
  var MIN_WIDTH = cfg.minWidth || 260; // never shrink narrower than this
  var MIN_VIEWPORT = cfg.minViewport || 1024; // below this, leave the theme alone

  var SELECTOR = '.product-gallery__item .lazy-image[data-ratio]';
  var selfDispatched = false;

  function clear(figures) {
    Array.prototype.forEach.call(figures, function (fig) {
      fig.style.maxWidth = '';
      fig.style.marginLeft = '';
      fig.style.marginRight = '';
    });
  }

  function fit() {
    var figures = document.querySelectorAll(SELECTOR);
    if (!figures.length) return;

    // On phones/tablets the gallery is full-bleed and scrolls naturally —
    // capping it there would just make images small for no reason.
    if (window.innerWidth < MIN_VIEWPORT) {
      clear(figures);
      return;
    }

    var maxHeight = Math.max(320, window.innerHeight - GUTTER);
    var changed = false;

    Array.prototype.forEach.call(figures, function (fig) {
      var ratio = parseFloat(fig.getAttribute('data-ratio'));
      if (!ratio || !isFinite(ratio) || ratio <= 0) return;

      var width = Math.max(MIN_WIDTH, Math.round(maxHeight * ratio));
      var next = width + 'px';
      if (fig.style.maxWidth !== next) {
        fig.style.maxWidth = next;
        fig.style.marginLeft = 'auto';
        fig.style.marginRight = 'auto';
        changed = true;
      }
    });

    // The slider caches its viewport height off .lazy-image, so it needs to
    // re-measure now that we've resized it. Guarded so our own resize
    // listener doesn't recurse.
    if (changed) {
      selfDispatched = true;
      window.dispatchEvent(new Event('resize'));
      selfDispatched = false;
    }
  }

  var timer = null;
  window.addEventListener('resize', function () {
    if (selfDispatched) return;
    clearTimeout(timer);
    timer = setTimeout(fit, 150);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fit);
  } else {
    fit();
  }

  // Media can arrive late (lazy loading, variant image swaps) and the theme
  // editor re-renders sections in place.
  window.addEventListener('load', fit);
  document.addEventListener('shopify:section:load', fit);
})();
